import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../layout/builders/Layout";
import { Search, MapPin, Briefcase, Star, Phone, Mail, Eye } from "lucide-react";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../../store/useAuthStore";
import toast from "react-hot-toast";

const ConsultantProfiles = () => {
    const navigate = useNavigate();
    const { token } = useAuthStore();
    const [consultants, setConsultants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterDistrict, setFilterDistrict] = useState("");

    useEffect(() => {
        fetchConsultants();
    }, []);

    const fetchConsultants = async () => {
        try {
            setLoading(true);
            const response = await axiosInstancev1.get("/consultants/profiles", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConsultants(response.data?.consultants || []);
        } catch (error) {
            console.error("Error fetching consultants:", error);
            toast.error("Failed to load consultants");
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (consultantId) => {
        navigate(`/builder/consultant-detail/${consultantId}`);
    };

    const handleViewSubscription = (consultantId) => {
        navigate(`/builder/consultant-subscription/${consultantId}`);
    };

    const filteredConsultants = consultants.filter(consultant => {
        const matchesSearch = !searchTerm || 
            consultant.engineer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            consultant.engineerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            consultant.name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesDistrict = !filterDistrict || 
            consultant.district?.toLowerCase() === filterDistrict.toLowerCase();
        
        return matchesSearch && matchesDistrict;
    });

    const districts = [...new Set(consultants.map(c => c.district).filter(Boolean))];

    return (
        <Layout>
            <div className="space-y-4 pb-4">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <h1 className="text-xl font-bold text-gray-900">Construction Consultants</h1>
                    <p className="text-sm text-gray-500 mt-1">Find and connect with construction consultants</p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* District Filter */}
                        <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <select
                                value={filterDistrict}
                                onChange={(e) => setFilterDistrict(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                            >
                                <option value="">All Districts</option>
                                {districts.map(district => (
                                    <option key={district} value={district}>{district}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Results Count */}
                <div className="flex items-center justify-between px-1">
                    <p className="text-sm text-gray-600">
                        {filteredConsultants.length} consultant{filteredConsultants.length !== 1 ? 's' : ''} found
                    </p>
                </div>

                {/* Consultants List */}
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                    </div>
                ) : filteredConsultants.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Briefcase className="text-gray-400" size={32} />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Consultants Found</h3>
                            <p className="text-gray-500 text-sm">
                                {searchTerm || filterDistrict 
                                    ? "Try adjusting your filters to find more consultants."
                                    : "No construction consultants are currently available."
                                }
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredConsultants.map((consultant) => (
                            <ConsultantCard
                                key={consultant.id}
                                consultant={consultant}
                                onViewDetails={handleViewDetails}
                                onViewSubscription={handleViewSubscription}
                            />
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};

const ConsultantCard = ({ consultant, onViewDetails, onViewSubscription }) => {
    const displayName = consultant.engineer_name || consultant.engineerName || consultant.name || "Consultant";
    const displayImage = consultant.profile_image || consultant.profileImage || consultant.engineerPhoto || "/assets/Support.png";
    const hasActiveSubscription = consultant.subscription_status === 'active';

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
            {/* Header with Image */}
            <div className="p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                    <img
                        src={displayImage}
                        alt={displayName}
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-200"
                        onError={(e) => { e.target.src = "/assets/Support.png"; }}
                    />
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">{displayName}</h3>
                        <div className="flex items-center gap-1 mt-1">
                            <MapPin size={12} className="text-gray-400" />
                            <span className="text-xs text-gray-500">{consultant.district || "N/A"}</span>
                        </div>
                        {hasActiveSubscription && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full mt-1">
                                <Star size={10} fill="currentColor" />
                                Active
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Details */}
            <div className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                    <Briefcase size={14} className="text-gray-400" />
                    <span className="text-gray-600 text-xs">
                        {consultant.qualification || "N/A"}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-gray-400" />
                    <span className="text-gray-600 text-xs">
                        {consultant.phone_number || consultant.contactNumber || consultant.phone || "N/A"}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                    <Mail size={14} className="text-gray-400" />
                    <span className="text-gray-600 text-xs truncate">
                        {consultant.email || "N/A"}
                    </span>
                </div>
                {consultant.experience && (
                    <div className="pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-500">
                            <span className="font-semibold">Experience:</span> {consultant.experience} years
                        </p>
                    </div>
                )}
            </div>

            {/* Actions */}
            <div className="p-4 pt-2 flex gap-2">
                <button
                    onClick={() => onViewDetails(consultant.id)}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5"
                >
                    <Eye size={14} />
                    View Details
                </button>
                <button
                    onClick={() => onViewSubscription(consultant.id)}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-200 transition-colors"
                >
                    Subscription
                </button>
            </div>
        </div>
    );
};

export default ConsultantProfiles;
